<template>
  <v-content class="pt-2">
    <TodoDetailDialog
      :opened.sync="todoDetailDialogOpened"
      :task="currentTask"
      @todo-updated="refreshTodoList()"
    />

    <v-layout>
      <v-flex xs12 sm3 class="pr-2">
        <div class="text-xs-left">Search</div>
        <v-text-field
          v-model="searchTerm"
          class="default-input subheading"
          solo
          hide-details
          placeholder="Search"
        />
      </v-flex>
      <v-flex xs2 class="px-2">
        <div class="text-xs-left">Entity</div>
        <v-select
          v-model="entityFilter"
          :items="allEntities"
          hide-details
          solo
          class="default-input"
          @change="refreshTodoList()"
        />
      </v-flex>
      <v-flex xs2 class="px-2">
        <div class="text-xs-left">Assigned to</div>
        <v-select
          v-model="assigneeFilter"
          :items="allFirmUsers"
          item-text="text"
          item-value="value"
          hide-details
          solo
          class="default-input"
          @change="refreshTodoList()"
        />
      </v-flex>

      <!-- Do not show priority filter for now
      <v-flex xs2 class="px-2">
        <div class="text-xs-left">
          Priority
        </div>
        <v-select
          v-model="priorityFilter"
          :items="allPriorities"
          hide-details
          solo
          class="default-input"
          @change="refreshTodoList()"
        />
      </v-flex>
      -->

      <v-flex xs2 class="px-2">
        <div class="text-xs-left">Status</div>
        <v-select
          v-model="statusFilter"
          :items="allStatuses"
          hide-details
          solo
          class="default-input"
          @change="refreshTodoList()"
        />
      </v-flex>
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :loading="tableLoading"
      :select-all="$vuetify.breakpoint.mdAndUp"
      v-model="selectedTasks"
      class="clients-table text-xs-left mt-3"
      :rows-per-page-items="[20, 50, 100]"
    >
      <template v-slot:items="props">
        <tr class="task-row" :key="props.index">
          <td class="hidden-sm-and-down" width="32px">
            <v-checkbox
              :input-value="selectedTaskIds.includes(props.item.id)"
              primary
              color="lightgrey"
              hide-details
              @change="toggleSelect(props.item, $event)"
            ></v-checkbox>
          </td>

          <td class="text-xs-left">
            <a
              :href="'/admin-dashboard/' + props.item.company.hash_id"
              class="client-name"
              @click.prevent="
                $router.push('/admin-dashboard/' + props.item.company.hash_id)
              "
            >
              <span class="client-name">
                {{ props.item.company.name }}
              </span>
            </a>
          </td>

          <td class="text-xs-left">
            <v-layout>
              <div
                class="task-title"
                v-html="props.item.title"
                @click="viewTask(props.item)"
              />
            </v-layout>
          </td>

          <!-- Do not show description column for now
          <td class="text-xs-left">
            <v-layout align-center>
              <div class="task-description" v-html="props.item.description"/>
            </v-layout>
          </td>
          -->

          <!-- Do not show status column for now
          <td class="text-xs-left">
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn
                  small
                  flat
                  class="status-btn"
                  v-on="on"
                >
                  <v-icon :color="getStatusColor(props.item.status)">
                    {{ getStatusIcon(props.item.status )}}
                  </v-icon>

                  <span class="ml-1 status text-none">
                    {{ getStatusText(props.item.status) }}
                  </span>
                </v-btn>
              </template>

              <v-list>
                <v-list-tile
                  v-for="[ statusName, statusValue ] in Object.entries(taskStatus)"
                  @click="updateStatus(props.item, statusValue)"
                >
                  <v-list-tile-title>
                    <v-layout>
                      <v-icon class="status-icon" :color="getStatusColor(statusValue)">
                        {{ getStatusIcon(statusValue) }}
                      </v-icon>
                      <span class="ml-1">{{ statusName }}</span>
                    </v-layout>
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </td>
          -->

          <td class="text-xs-left">
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn small flat class="status-btn text-none" v-on="on">
                  <span class="client-name">
                    {{ props.item.assigned_user.full_name }}
                  </span>
                </v-btn>
              </template>

              <v-list>
                <v-list-tile
                  v-for="firmUser in firmUsers"
                  :key="firmUser.id"
                  @click="updateAssignee(props.item, firmUser)"
                >
                  <v-list-tile-title>
                    <span class="ml-1">{{ firmUser.name }}</span>
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </td>

          <!-- Do not show priority column for now
          <td class="text-xs-left" :class="'priority__' + getPriorityText(props.item.priority)">
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn
                  small
                  flat
                  class="status-btn text-none"
                  v-on="on"
                >
                  <v-icon small class="priority-icon" :class="props.item.priority === 3 ? 'warning--text' : 'grey--text'">{{ getPriorityIcon(props.item.priority) }}</v-icon>
                  <span class="body-1">{{ getPriorityText(props.item.priority) }}</span>
                </v-btn>
              </template>

              <v-list>
                <v-list-tile
                  v-for="[ priorityName, priorityValue ] in Object.entries(taskPriority)"
                  @click="updatePriority(props.item, priorityValue)"
                >
                  <v-list-tile-title>
                    <v-layout>
                      <v-icon small class="priority-icon" :class="priorityValue === 3 ? 'warning--text' : 'grey--text'">
                        {{ getPriorityIcon(priorityValue) }}
                      </v-icon>
                      <span class="ml-1">{{ priorityName }}</span>
                    </v-layout>
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </td>
          -->

          <td class="text-xs-left">
            <span>{{ moment(props.item.created_at).fromNow() }}</span>
          </td>

          <td class="text-xs-left">
            <v-layout>
              <v-btn
                v-if="props.item.status === taskStatus.Todo"
                small
                color="primary"
                class="text-none mr-1"
                @click="updateStatus(props.item, taskStatus.Completed)"
              >
                <v-icon small class="mr-2">check</v-icon>
                Mark Completed
              </v-btn>

              <v-btn
                v-else-if="props.item.status !== taskStatus.Todo"
                small
                color="green"
                class="white--text text-none mr-1"
                @click="updateStatus(props.item, taskStatus.Todo)"
              >
                <v-icon small class="mr-2">restore</v-icon>
                Restore
              </v-btn>

              <v-tooltip bottom v-if="props.item.status === taskStatus.Todo">
                <v-btn
                  slot="activator"
                  small
                  outline
                  icon
                  color="primary"
                  @click="updateStatus(props.item, taskStatus.Dismissed)"
                >
                  <v-icon small>close</v-icon>
                </v-btn>
                <span>Dismiss</span>
              </v-tooltip>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-content>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import API from '@/api';
import TodoDetailDialog from '@/components/dialogs/TodoDetailDialog';
import { taskPriority, taskStatus } from '@/constants/variables';
import upperFirst from 'lodash/upperFirst';
import axios from 'axios';
import firebase from 'firebase';

export default {
  components: {
    TodoDetailDialog,
  },

  props: {
    tableLoading: {
      type: Boolean,
    },

    todoTasks: {
      type: Array,
    },
  },

  data() {
    return {
      currentUserFirm: null,
      firmUsers: [],

      taskPriority,
      taskStatus,

      todoDetailDialogOpened: false,
      currentTask: null,
      selectedTasks: [],

      // For live updates
      newTasksRef: null,
      listeningToNewTasks: null,

      searchTerm: '',
      entityFilter: 'All',
      assigneeFilter: 'All',
      tagFilters: [],
      priorityFilter: 'All',
      statusFilter: 0,

      headers: [
        {
          text: 'Entity name',
          align: 'left',
          value: 'company.name',
        },
        {
          text: 'Task',
          align: 'left',
          value: 'title',
        },
        {
          text: 'Assigned to',
          align: 'left',
          value: 'assigned_user.full_name',
        },
        {
          text: 'Added',
          align: 'left',
          value: 'created_at',
        },
        {
          text: 'Actions',
          align: 'left',
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    selectedTaskIds() {
      return this.selectedTasks.map(task => task.id);
    },

    allPriorities() {
      const priorities = Object.entries(taskPriority).map(([text, value]) => {
        return {
          text,
          value,
        };
      });

      priorities.unshift({
        text: 'All',
        value: 'All',
      });

      return priorities;
    },

    allStatuses() {
      const statuses = Object.entries(taskStatus).map(([text, value]) => {
        return {
          text,
          value,
        };
      });

      return statuses;
    },

    allFirmUsers() {
      return [
        {
          text: 'All',
          value: 'All',
        },

        ...this.firmUsers.map(firmUser => {
          return {
            text: firmUser.name,
            value: firmUser.id,
          };
        }),
      ];
    },

    allEntities() {
      const allEntities = [
        {
          text: 'All',
          value: 'All',
        },

        ...this.todoTasks.map(item => {
          return {
            text: item.company.name,
            value: item.company.id,
          };
        }),
      ];

      return uniqBy(allEntities, 'value');
    },

    allTags() {
      const allTags = this.todoTasks.flatMap(item => {
        return item.tags;
      });

      return uniq(allTags);
    },

    filteredItems() {
      const searchTerm = this.searchTerm.toLowerCase();

      return this.todoTasks.filter(row => {
        let rowExists = true;

        if (rowExists && searchTerm.length) {
          rowExists =
            row.title.toLowerCase().includes(searchTerm) ||
            row.company.name.toLowerCase().includes(searchTerm);
        }

        if (rowExists && this.entityFilter !== 'All') {
          rowExists = row.company_id === this.entityFilter;
        }

        if (rowExists && this.assigneeFilter !== 'All') {
          rowExists = row.assigned_user_id === parseInt(this.assigneeFilter);
        }

        if (rowExists && this.tagFilters.length > 0) {
          rowExists =
            typeof row.tags.find(tag => this.tagFilters.includes(tag)) !==
            'undefined';
        }

        if (rowExists && this.priorityFilter !== 'All') {
          rowExists = row.priority === this.priorityFilter;
        }

        if (rowExists && this.statusFilter !== 'All') {
          rowExists = row.status === this.statusFilter;
        }

        return rowExists;
      });
    },
  },

  methods: {
    moment,
    isEmpty,

    viewTask(task) {
      this.todoDetailDialogOpened = true;
      this.currentTask = task;
    },

    getPriorityText(priorityValue) {
      const priority = Object.entries(taskPriority).find(([key, value]) => {
        if (value === parseInt(priorityValue)) {
          return key;
        }
      });

      return upperFirst(priority ? priority[0].toLowerCase() : '');
    },

    getPriorityIcon(priorityValue) {
      const icons = {
        0: 'signal_cellular_alt_1_bar',
        1: 'signal_cellular_alt_2_bar',
        2: 'signal_cellular_alt',
        3: 'warning',
      };

      return icons[priorityValue];
    },

    getStatusText(statusValue) {
      const status = Object.entries(taskStatus).find(([key, value]) => {
        if (value === parseInt(statusValue)) {
          return key;
        }
      });

      return upperFirst(status ? status[0].toLowerCase() : '');
    },

    getStatusColor(status) {
      const colors = {
        0: 'grey',
        1: 'green',
        2: 'grey',
      };

      return colors[status];
    },

    getStatusIcon(status) {
      const icons = {
        0: 'radio_button_unchecked',
        1: 'check_circle',
        2: 'cancel',
      };

      return icons[status];
    },

    refreshTodoList() {
      this.$emit('refresh-table');
    },

    async updateStatus(task, status) {
      await axios.patch(`/todo_tasks/${task.id}`, {
        ...task,
        status: status,
      });

      this.refreshTodoList();
    },

    async updateAssignee(task, firmUser) {
      await axios.patch(`/todo_tasks/${task.id}`, {
        ...task,
        assigned_user_id: firmUser.id,
      });

      this.refreshTodoList();
    },

    async updatePriority(task, priority) {
      await axios.patch(`/todo_tasks/${task.id}`, {
        ...task,
        priority,
      });

      this.refreshTodoList();
    },

    getActionName(action) {
      if (action.type === 'view-questionnaire') {
        return 'View Questionnaire';
      }
    },

    async deleteTask(task) {
      await axios.delete(`/todo_tasks/${task.id}`, {
        data: {
          company_id: task.company_id,
        },
      });

      this.refreshTodoList();
    },

    async getAllFirmUsers() {
      const response = await API.firm.getAllUsers();
      this.firmUsers = response.data.users;
    },

    toggleSelect(target, selected) {
      if (selected) {
        this.selectedTasks = uniqBy(this.selectedTasks.concat(target), 'id');
      } else {
        this.selectedTasks = this.selectedTasks.filter(
          task => task.id !== target.id,
        );
      }
    },

    startListeningNewTasks() {
      const database = firebase.database();

      this.newTasksRef = database.ref(
        `firms/${this.currentUserFirm.id}/newTasks`,
      );

      this.listeningToNewTasks = this.newTasksRef.on('value', async () => {
        this.refreshTodoList();
      });
    },

    async getCurrentUserFirm() {
      const response = await API.firm.getCurrentUserFirm();
      this.currentUserFirm = response.data;
    },
  },

  async mounted() {
    await this.getCurrentUserFirm();
    this.startListeningNewTasks();
  },

  async unmounted() {
    if (this.newTasksRef && this.listeningToNewTasks) {
      this.newTasksRef.off('value', this.listeningToNewTasks);
    }
  },

  watch: {
    selectedTasks() {
      this.$emit(
        'on-selection-change',
        this.selectedTasks.map(task => task.id),
      );
    },
  },

  async created() {
    await this.getAllFirmUsers();
    //this.assigneeFilter = this.currentUser.id;
  },
};
</script>

<style lang="postcss" scoped>
.task-row {
}

.task-row > td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.client-name {
  text-decoration: none;
}

.task-title {
  cursor: pointer;
}

.task-description {
  margin-top: 8px;

  & >>> p {
    margin-bottom: 8px;
  }
}

.tag {
  margin-left: 0;
  margin-right: 6px;
  flex: 0 !important;
}

.priority {
  margin-left: 0;
  margin-right: 6px;
  flex: 0 !important;
}

.priority-icon {
  margin-right: 3px;
}

.status-icon {
  margin-right: 3px;
}

.status-btn {
  margin: 0;
  margin-left: -8px;
  padding: 8px;

  & >>> .v-btn__content {
    justify-content: start;
  }
}

.task-action {
  margin: 0;
}
</style>
